import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, User, BookOpen, Tv, Mail, Heart, Home, Briefcase, Calendar, ShoppingBag, GraduationCap } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6a4505e5-3f70-4447-9214-c7fe0572ffa6/artifacts/4ongzfvs_THE%20MATRIX%20UNIVERSITY%20VIRTUAL%20REALITY%20LOGO.png";

// Simplified main navigation - VR Campus (Metaversity) separate from VR Chat
const navItems = [
  { label: 'Services', path: '/services' },
  { label: 'Memberships', path: '/courses' },
  { label: 'International Reseller', path: '/international-reseller' },
  { label: 'Sponsorship', path: '/sponsorship' },
  { label: 'Merchandise', path: '/merchandise' },
  { label: 'About', path: '/about-jason' },
  { label: 'Contact', path: '/contact' },
];

// Footer navigation - secondary items
const footerNavItems = [
  { label: 'Services', path: '/services' },
  { label: 'Memberships', path: '/courses' },
  { label: 'International Reseller', path: '/international-reseller' },
  { label: 'Sponsorship', path: '/sponsorship' },
  { label: 'Merchandise', path: '/merchandise' },
  { label: 'About Dr. Jason', path: '/about-jason' },
  { label: 'Metaversity VR Campus', path: '/vr-campus' },
  { label: 'VR Demo Videos', path: '/vr-demo-videos' },
  { label: 'VR Chat World', path: '/vr-chat-meta-quest' },
  { label: 'Events', path: '/events' },
  { label: 'Media', path: '/media' },
  { label: 'E-Books', path: '/ebooks' },
  { label: "Children's Books", path: '/books' },
  { label: 'Contact', path: '/contact' },
  { label: 'Donate', path: '/donate' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="header">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="The Matrix University"
              className="h-14 w-14 object-contain"
            />
            <span className="hidden md:block text-lg font-serif font-semibold text-white">
              The Matrix University
            </span>
          </Link>

          {/* Desktop Navigation - Simplified */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`nav-link px-4 py-2 rounded-lg ${isActive('/') ? 'text-white bg-primary/20' : ''}`}
              data-testid="nav-home"
            >
              Home
            </Link>
            
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className={`nav-link px-4 py-2 rounded-lg ${isActive(item.path) ? 'text-white bg-primary/20' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Navigation - Simplified */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2">
                <Link
                  to="/"
                  className={`block px-4 py-3 rounded-lg ${isActive('/') ? 'bg-primary/20 text-white' : 'text-white/80'}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-home"
                >
                  <Home className="w-5 h-5 inline mr-3" />
                  Home
                </Link>
                
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    className={`block px-4 py-3 rounded-lg ${
                      isActive(item.path) ? 'bg-primary/20 text-white' : 'text-white/80'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-card border-t border-border py-12" data-testid="footer">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={LOGO_URL}
                alt="The Matrix University"
                className="h-12 w-12 object-contain"
              />
              <span className="text-xl font-serif font-semibold text-white">
                The Matrix University
              </span>
            </div>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-md">
              Where Education Meets the Future. Immersive education through VR, AI, culture, and global learning.
            </p>
            <p className="text-white mt-4 font-semibold">
              Dr. Jason E. Williams, Ed.D.
            </p>
            <p className="text-muted-foreground text-sm">
              Doctor of Education in Organizational Innovation
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Explore</h4>
            <ul className="space-y-2">
              <li><Link to="/services" className="text-muted-foreground hover:text-white transition-colors text-sm">Services</Link></li>
              <li><Link to="/courses" className="text-muted-foreground hover:text-white transition-colors text-sm">Memberships</Link></li>
              <li><Link to="/vr-campus" className="text-muted-foreground hover:text-white transition-colors text-sm">Metaversity VR Campus</Link></li>
              <li><Link to="/vr-demo-videos" className="text-muted-foreground hover:text-white transition-colors text-sm">VR Demo Videos</Link></li>
              <li><Link to="/events" className="text-muted-foreground hover:text-white transition-colors text-sm">Events</Link></li>
              <li><Link to="/media" className="text-muted-foreground hover:text-white transition-colors text-sm">Media</Link></li>
            </ul>
          </div>

          {/* More Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">More</h4>
            <ul className="space-y-2">
              <li><Link to="/about-jason" className="text-muted-foreground hover:text-white transition-colors text-sm">About Dr. Jason</Link></li>
              <li><Link to="/ebooks" className="text-muted-foreground hover:text-white transition-colors text-sm">E-Books</Link></li>
              <li><Link to="/books" className="text-muted-foreground hover:text-white transition-colors text-sm">Children's Books</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-white transition-colors text-sm">Contact</Link></li>
              <li><Link to="/donate" className="text-muted-foreground hover:text-white transition-colors text-sm">Donate</Link></li>
            </ul>
          </div>
        </div>

        {/* Footer Bottom - Responsive Layout */}
        <div className="border-t border-border mt-8 pt-8">
          {/* Mobile: Stack vertically, Desktop: Row */}
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between md:gap-4">
            {/* Copyright */}
            <p className="text-muted-foreground text-sm text-center md:text-left">
              © {new Date().getFullYear()} The Matrix University. All rights reserved.
            </p>
            
            {/* Partner Logos */}
            <div className="flex items-center gap-3">
              <span className="text-muted-foreground text-xs hidden md:inline">Partners:</span>
              <a 
                href="https://www.victoryxr.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/h02r640c_IMG_4656.jpeg" 
                  alt="VictoryXR" 
                  className="h-6 md:h-8 w-auto rounded"
                />
              </a>
              <a 
                href="https://www.meta.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_edu-portal-276/artifacts/4l2csir8_IMG_4658.jpeg" 
                  alt="Meta" 
                  className="h-6 md:h-8 w-auto rounded"
                />
              </a>
            </div>
            
            {/* Admin Link */}
            <Link to="/admin" className="text-muted-foreground hover:text-white transition-colors text-sm">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
